import { Controller } from "@hotwired/stimulus"

// Fades a below-the-fold section in when it scrolls into view.
//
// The hidden state (opacity-0) is applied here in JS — NOT in the server markup —
// so the section's content (headings, copy) is always present and visible in the
// raw HTML for crawlers and JS-disabled users. We deliberately fade with OPACITY,
// not `visibility:hidden`: visibility:hidden zeroes out heading text for crawlers
// reading rendered text (it produced an SEO "empty H1/H2/H3" report). The real
// off-screen perf win lives in the `.lazy-section` CSS (`content-visibility: auto`),
// which is retained and is crawler-safe.
//
// Usage: <div data-controller="lazy-section" class="lazy-section">...</div>
export default class extends Controller {
  static values = {
    threshold: { type: Number, default: 0.1 },
    rootMargin: { type: String, default: "200px" }
  }

  connect() {
    this.element.classList.add("opacity-0", "transition-opacity", "duration-700", "ease-out")

    // Paint the hidden state before observing, otherwise a section already in the
    // viewport reveals before opacity-0 is ever rendered (no transition).
    requestAnimationFrame(() => {
      this.observer = new IntersectionObserver(
        this.reveal.bind(this),
        {
          rootMargin: this.rootMarginValue,
          threshold: this.thresholdValue
        }
      )
      this.observer.observe(this.element)
    })
  }

  reveal(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.element.classList.remove("opacity-0")
        this.observer.disconnect()
      }
    })
  }

  disconnect() {
    this.observer?.disconnect()
  }
}
